import { Link } from "react-router-dom";
import { isReadOnlyRole } from "@bitacorapro/api-types";
import { ChecklistTemplatesPanel } from "../components/ChecklistTemplatesPanel";
import { Icon } from "../components/icons";
import { useMyOrganization, useRecipe } from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { canImportUnits } from "../lib/capabilities";
import { orgDisplayName } from "../lib/orgMarkLogo";

type IconName = keyof typeof Icon;

type RequestCardSpec = {
  key: string;
  icon: IconName;
  title: string;
  sub: string;
  actions: Array<{
    label: string;
    icon?: IconName;
    variant: "primary" | "secondary";
    /** In-app destination. Without it the button is still the mailer stub. */
    to?: string;
  }>;
};

// ADR-0144 §2 — a recipe with a unit profile (postventa) loads its units
// itself through the importer, so the facility «Cargar nuevas tiendas»
// mail request is replaced by a link to it.
const UNIT_IMPORT_CARD: RequestCardSpec = {
  key: "cargar-unidades",
  icon: "Units",
  title: "Cargar unidades",
  sub: "Crea o actualiza las unidades de tus proyectos desde una planilla: tipología, estacionamientos, bodegas, fechas de recepción DOM, inscripción CBR y entrega, y el contacto del propietario.",
  actions: [
    { label: "Ir a cargar unidades", icon: "Arrow", variant: "primary", to: "/carga-unidades" },
  ],
};

const REQUEST_CARDS: RequestCardSpec[] = [
  {
    key: "cargar-tiendas",
    icon: "Store",
    title: "Cargar nuevas tiendas",
    sub: "Para sumar tiendas necesitamos código, dirección, zona, marca, m² y jefe de tienda asignado. Puedes enviarlas en planilla o pasarnos un CSV.",
    actions: [
      { label: "Descargar plantilla", icon: "Download", variant: "secondary" },
      { label: "Solicitar carga", icon: "Mail", variant: "primary" },
    ],
  },
  {
    key: "editar-taxonomia",
    icon: "Tag",
    title: "Editar taxonomía de categorías",
    sub: "Las observaciones se clasifican según las categorías de Facility de tu vertical. Si necesitas agregar, renombrar o subdividir alguna, abre una solicitud con la justificación.",
    actions: [
      { label: "Ver taxonomía actual", icon: "External", variant: "secondary" },
      { label: "Proponer cambio", icon: "Mail", variant: "primary" },
    ],
  },
  {
    key: "areas-por-marca",
    icon: "Database",
    title: "Configurar áreas por marca",
    sub: "Si una marca específica tiene áreas distintas, podemos cargarlas como set default por marca.",
    actions: [{ label: "Solicitar override", icon: "Mail", variant: "primary" }],
  },
  {
    key: "foco-ia",
    icon: "Sparkles",
    title: "Ajustar foco de la IA",
    sub: "La IA puede priorizar ciertas categorías según las prioridades de tu operación. Cuéntanos qué te interesa y lo ajustamos.",
    actions: [
      {
        label: "Conversar con equipo BitácoraPro",
        icon: "Mail",
        variant: "primary",
      },
    ],
  },
  {
    key: "onboarding-masivo",
    icon: "Users",
    title: "Onboarding masivo de jefes de tienda",
    sub: "Si necesitas invitar a más de 20 personas con acceso de lectura a sus tiendas, te ayudamos con el proceso desde el admin.",
    actions: [{ label: "Iniciar onboarding masivo", icon: "Mail", variant: "primary" }],
  },
];

function recipeLabel(slug: string | undefined): string {
  if (!slug) return "—";
  if (slug === "facility_management") return "Facility Management";
  if (slug === "residential_rental") return "Arriendos residenciales";
  if (slug === "broker_postventa") return "Broker — postventa";
  return slug;
}

export function Configuracion() {
  const session = useSession();
  const { data: recipe } = useRecipe();
  const org = session?.organization;
  const hasUnitImport = canImportUnits(recipe, org?.role, session?.user.platformAdmin);
  const cards = REQUEST_CARDS.map((c) =>
    c.key === "cargar-tiendas" && recipe?.unitProfile ? UNIT_IMPORT_CARD : c
  ).filter((c) => c !== UNIT_IMPORT_CARD || hasUnitImport);
  // For a brand-scoped caller the brand is "their organization" (ADR-0164
  // §6) — same name the sidebar shows.
  const myOrg = useMyOrganization();
  const orgName = myOrg.isPending ? "—" : orgDisplayName(myOrg.data, org?.name);
  // Read-only roles (ADR-0164) cannot ask for configuration changes: the
  // nav already hides this screen from them (lib/nav.ts), and reaching it
  // by URL shows only the organization data, without the request cards.
  const readOnly = isReadOnlyRole(org?.role ?? "");

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">Configuración · {orgName}</div>
          <h1 className="page-header__title">Configuración</h1>
          <p className="page-header__sub">
            Las taxonomías y la carga de tiendas son administradas por el equipo BitácoraPro.
            {!readOnly &&
              " Solicita los cambios desde aquí y respondemos en menos de 24 hr hábiles."}
          </p>
        </div>
      </div>

      <div className="stack-gap">
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Datos de la organización</div>
          </div>
          <div className="panel__body panel__body--info-list">
            <InfoRow k="Nombre" v={orgName} />
            <InfoRow
              k="Vertical activa"
              v={recipeLabel(undefined /* recipe slug not yet on session */)}
            />
            <InfoRow k="Tu rol en la organización" v={org?.role ?? "—"} mono />
          </div>
        </div>

        <ChecklistTemplatesPanel />

        {!readOnly && (
          <div>
            <div className="config-section-head">
              <h2 className="config-section-head__title">Solicitudes al admin BitácoraPro</h2>
              <span className="cell-meta">SLA 24 hr hábiles</span>
            </div>
            <div className="stack-gap">
              {cards.map((r) => (
                <RequestCard key={r.key} spec={r} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="info-row">
      <div className="info-row__k">{k}</div>
      <div className={`info-row__v ${mono ? "info-row__v--mono" : ""}`}>{v}</div>
    </div>
  );
}

function RequestCard({ spec }: { spec: RequestCardSpec }) {
  const IconC = Icon[spec.icon];
  return (
    <div className="req-card">
      <div className="req-card__icon">
        <IconC s={20} />
      </div>
      <div className="req-card__body">
        <div className="req-card__title">{spec.title}</div>
        <div className="req-card__sub">{spec.sub}</div>
        <div className="req-card__actions">
          {spec.actions.map((a) => {
            const AIcon = a.icon ? Icon[a.icon] : null;
            if (a.to) {
              return (
                <Link key={a.label} to={a.to} className={`btn btn--${a.variant} btn--sm`}>
                  {AIcon && <AIcon s={12} />} {a.label}
                </Link>
              );
            }
            return (
              <button
                key={a.label}
                type="button"
                className={`btn btn--${a.variant} btn--sm`}
                title="Stub — abre el flujo real cuando el mailer surface esté wireado"
              >
                {AIcon && <AIcon s={12} />} {a.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
